import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  { key: 'allgemein', label: 'Allgemein (branchenübergreifend)' },
  { key: 'elektro', label: 'Elektro' },
  { key: 'shk', label: 'SHK / Sanitär-Heizung-Klima' },
  { key: 'holz', label: 'Holz / Tischler' },
  { key: 'metall', label: 'Metall / Schweißer' },
  { key: 'bau', label: 'Bau / Rohbau' },
];

/**
 * Blanko-Kurs-Erstellungs-Dialog.
 * Ersetzt das window.prompt durch einen sauberen Modal-Flow.
 */
export const BlankCourseDialog = ({ open, onOpenChange, onCreated }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('allgemein');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const reset = () => {
    setTitle('');
    setCategory('allgemein');
    setDescription('');
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast({ title: 'Titel fehlt', description: 'Bitte gib einen Kurs-Titel ein.', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const res = await apiFetch(`${API}/api/admin/create-blank-course`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          category,
          branche: category,
          description: description.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: '✓ Blanko-Kurs erstellt', description: `${data.title} mit 3 Lektions-Vorlagen. Editor öffnet sich…` });
        reset();
        onOpenChange(false);
        if (onCreated) onCreated(data.module_id, data.title);
      } else {
        toast({ title: 'Fehler', description: data.detail || 'Erstellung fehlgeschlagen', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Fehler', description: String(e), variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-blue-500/30 max-w-lg text-white" data-testid="blank-course-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-full bg-blue-500/20 border border-blue-500/40">
              <Sparkles className="h-5 w-5 text-blue-300" />
            </div>
            <DialogTitle className="text-xl text-blue-200">Neuer Blanko-Kurs</DialogTitle>
          </div>
          <DialogDescription className="text-gray-400">
            Wir erzeugen 3 leere Lektions-Vorlagen mit fertigem Quiz-Formular. Du füllst nur noch Titel,
            Text und Antworten aus.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-gray-300 text-sm">Kurs-Titel*</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Sicheres Arbeiten mit Winkelschleifer"
              className="bg-gray-800 border-gray-700 text-white"
              data-testid="blank-title-input"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-gray-300 text-sm">Branche / Kategorie</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-2"
              data-testid="blank-category-select"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-gray-300 text-sm">Kurz-Beschreibung (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Was lernen deine Mitarbeiter? Max. 200 Zeichen…"
              className="bg-gray-800 border-gray-700 text-white text-sm"
              rows={2}
              maxLength={200}
              data-testid="blank-description-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { reset(); onOpenChange(false); }}
              className="border-gray-700 text-gray-300"
              data-testid="blank-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !title.trim()}
              className="bg-blue-500 hover:bg-blue-400 text-white font-semibold disabled:opacity-50"
              data-testid="blank-create-btn"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Erstellen &amp; öffnen <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BlankCourseDialog;
